import {useMemo, useEffect} from 'react';
import {addTagToMap, removeTagFromMap, updateDefaultValueForTag} from '@Components/merge-tags/merge-tags-reducer'; // Update to your actual store path
import type {MergeTag} from '@Components/merge-tags/merge-tags.types';
import {useUserCustomFields} from '@Hooks/user-custom-fields/useUserCustomFields';
import {lowerCaseFirstLetter} from '@Utils/string.util';
import {MERGE_TAGS} from '@/email-maker/grapesjs-email-editor-plugin/src/var';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {RootState} from '@/store';

interface UseMergeTags {
  addMergeTag: (mergeTagKey: string) => void;
  removeMergeTag: (mergeTagKey: string) => void;
  mergeTags: MergeTag[];
  mergeTagOptions: MergeTag[];
  isLoading: boolean;
  defaultValues: Record<string, string>;
  updateDefaultValue: (mergeTagKey: string, defaultValue: string) => void;
}

const useMergeTags = (ccid?: string): UseMergeTags => {
  const {customFields, areLoaded, populateUserCustomFields} = useUserCustomFields();
  const dispatch = useAppDispatch();

  const customFieldMergeTags = useMemo(() => {
    return customFields.map((cf): MergeTag => {
      return {
        mergeTagKey: lowerCaseFirstLetter(cf.label.trim().replace(/\s/g, '')),
        label: cf.label,
        icon: 'icon-tags',
      };
    });
  }, [customFields]);

  const mergeTags = useMemo(() => {
    return [...MERGE_TAGS, ...customFieldMergeTags];
  }, [customFieldMergeTags]);

  const mergeTagsInComponent = useAppSelector(
    useMemo(
      () =>
        (state: RootState): string[] | undefined => {
          if (!ccid) {
            return;
          }
          return state.emailMakerMergeTags.mergeTagsHashMap[ccid];
        },
      [ccid]
    )
  );

  const defaultValues = useAppSelector(
    useMemo(
      () =>
        (state: RootState): Record<string, string> => {
          return state.emailMakerMergeTags.defaultValueHashMap;
        },
      [ccid]
    )
  );

  const selectedMergeTags = useMemo(() => {
    if (!mergeTagsInComponent) {
      return [];
    }

    return mergeTags.filter((tag) => mergeTagsInComponent.includes(tag.mergeTagKey));
  }, [mergeTags, mergeTagsInComponent]);

  const addMergeTag = (mergeTagKey: string): void => {
    if (!ccid) {
      return;
    }
    dispatch(addTagToMap({ccid, mergeTagKey}));
  };

  const removeMergeTag = (mergeTagKey: string): void => {
    if (!ccid) {
      return;
    }

    dispatch(removeTagFromMap({ccid, mergeTagKey}));
  };

  const updateDefaultValue = (mergeTagKey: string, defaultValue: string): void => {
    dispatch(updateDefaultValueForTag({mergeTagKey, value: defaultValue}));
  };

  useEffect(() => {
    if (areLoaded) {
      return;
    }
    void populateUserCustomFields();
  }, [areLoaded, populateUserCustomFields]);

  return {
    mergeTagOptions: mergeTags,
    mergeTags: selectedMergeTags,
    addMergeTag,
    removeMergeTag,
    isLoading: !areLoaded,
    defaultValues,
    updateDefaultValue,
  };
};

export default useMergeTags;
